import React, { useState } from "react";
import { Helmet } from "react-helmet";
import "./Trial.css";
import celebrate from "./assets/celebrate.png";
import { Link } from "react-router-dom";
import { supabase } from "./supabaseClient";

const Trial = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    businessEmail: "",
    companyName: "",
    websiteUrl: "",
    businessType: "",
    workedWithSeo: "",
    primaryGoal: "",
  });
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (error) {
      setError("");
    }
  };

  const handleSubmit = async () => {
    if (
      !formData.fullName.trim() ||
      !formData.businessEmail.trim() ||
      !formData.companyName.trim() ||
      !formData.websiteUrl.trim() ||
      !formData.businessType ||
      !formData.workedWithSeo
    ) {
      setError("Please fill out all required fields before submitting.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.businessEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    if (formData.primaryGoal.trim()) {
      const wordCount = formData.primaryGoal.trim().split(/\s+/).length;
      if (wordCount > 150) {
        setError("Primary goal must be 150 words or less.");
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const { data, error: supabaseError } = await supabase
        .from("trial_requests")
        .insert([
          {
            full_name: formData.fullName,
            business_email: formData.businessEmail,
            company_name: formData.companyName,
            website_url: formData.websiteUrl,
            business_type: formData.businessType,
            worked_with_seo_before: formData.workedWithSeo,
            primary_goal: formData.primaryGoal || null,
          },
        ]);

      if (supabaseError) throw supabaseError;

      setFormData({
        fullName: "",
        businessEmail: "",
        companyName: "",
        websiteUrl: "",
        businessType: "",
        workedWithSeo: "",
        primaryGoal: "",
      });

      setShowModal(true);
    } catch (err) {
      console.error("Error submitting form:", err);
      setError("Failed to submit form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const handleModalOutsideClick = (e) => {
    if (e.target.className === "modal-overlay") {
      closeModal();
    }
  };

  return (
    <div className="page-wrapper">
      <Helmet>
        <meta
          name="description"
          content="Start your trial with Attoli today and experience the expertise in SEO services tailored for finance businesses. Boost your online visibility and connect with more customers."
        />
        <title>SEO Trial Packages | Attoli</title>
        <link rel="canonical" href="https://attoli.com/trial" />
      </Helmet>

      <div className="trial-page-container">
        <div className="trial-page-content">
          <div className="trial-form-section">
            <h1 className="trial-page-title">Trial Package</h1>

            <p className="trial-page-description">
              By completing this short form, we can assess if our relationship
              is a good match. Once approved an email will be sent in the next
              24 hours with further instructions regarding next steps and
              payment.
            </p>
            <p className="trial-page-reassurance">
              No long-term commitment. No phone calls unless you ask for one.
            </p>

            {error && <div className="error-message">{error}</div>}

            <div className="trial-form">
              <div className="input-group">
                <input
                  type="text"
                  name="fullName"
                  placeholder="Full Name *"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className="trial-input"
                />
              </div>

              <div className="input-group">
                <input
                  type="email"
                  name="businessEmail"
                  placeholder="Business Email *"
                  value={formData.businessEmail}
                  onChange={handleInputChange}
                  className="trial-input"
                />
              </div>

              <div className="input-group">
                <input
                  type="text"
                  name="companyName"
                  placeholder="Company Name *"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  className="trial-input"
                />
              </div>

              <div className="input-group">
                <input
                  type="url"
                  name="websiteUrl"
                  placeholder="Website URL *"
                  value={formData.websiteUrl}
                  onChange={handleInputChange}
                  className="trial-input"
                />
              </div>

              <div className="input-group">
                <select
                  name="businessType"
                  value={formData.businessType}
                  onChange={handleInputChange}
                  className="trial-input trial-select"
                >
                  <option value="">Business Type *</option>
                  <option value="Insurance Agency">Insurance Agency</option>
                  <option value="Accounting / Bookkeeping">
                    Accounting / Bookkeeping
                  </option>
                  <option value="Mortgage Broker">Mortgage Broker</option>
                  <option value="Wealth Management">Wealth Management</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="input-group">
                <select
                  name="workedWithSeo"
                  value={formData.workedWithSeo}
                  onChange={handleInputChange}
                  className="trial-input trial-select"
                >
                  <option value="">
                    Have you worked with an SEO agency before? *
                  </option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>

              <div className="input-group">
                <textarea
                  name="primaryGoal"
                  placeholder="What would make this trial successful for you? (optional)"
                  value={formData.primaryGoal}
                  onChange={handleInputChange}
                  className="trial-textarea"
                  rows="5"
                />
              </div>

              <button
                type="button"
                onClick={handleSubmit}
                className="trial-submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Request Trial Review"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={handleModalOutsideClick}>
          <div className="modal-content">
            <button className="modal-close" onClick={closeModal}>
              ×
            </button>
            <div className="modal-image-container">
              <img src={celebrate} alt="Mailbox" className="modal-image" />
            </div>
            <h2 className="modal-title">Amazing!</h2>
            <p className="modal-text">
              Thank you for requesting a trial with us. We’ll review your
              details and email back you within 24 hours with next steps and
              payment instructions
            </p>
          </div>
        </div>
      )}

      <footer className="footer">
        <div className="footer-content">
          <div className="footer-header">
            <h3 className="footer-logo">attoli</h3>
            <p className="footer-quote">
              "You're driving with the Driving Crooner, baby" - Tim Robinson
            </p>
            <p className="footer-description">
              Attoli is an Ottawa-based SEO company helping finance businesses
              across Canada create visibility, connect with customers, and grow
              online with proven digital marketing and SEO engine optimization
              strategies.
            </p>
          </div>

          <div className="footer-links">
            <Link to="/privacy-policy" className="footer-link">
              Privacy & Policy
            </Link>
            <Link to="/contact" className="footer-link">
              Contact
            </Link>

            <a
              href="https://calendly.com/attoli/30min"
              target="_blank"
              className="footer-link"
              rel="noreferrer"
            >
              Book a Meeting
            </a>
          </div>

          <div className="footer-copyright">
            © 2025 Attoli. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Trial;
